import { Fragment, memo, useEffect, useMemo, useRef } from 'react';
import { areEqual } from 'react-window';
import { memoize, uniq } from 'lodash';
import { useTheme } from 'styled-components';
import { Form } from 'antd5';
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseOutlined,
  LoadingOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { ErrorAlert, Tooltip, WarningAlert } from 'components/ui';

import {
  ErrorWrapper,
  FilesSelected,
  SelectedFile,
  StyledList,
  StyledFilename,
  StyledRemoveButton,
  StyledFileNameRow,
} from './SelectedFileList.style';
import { FileUploadProgressSummary } from '../FileUploadProgressSummary';
import { DRAG_AND_DROP_DIRECTORY_ERROR, useUploadContext } from 'components/FileUploader';
import { FilteredFiles, UploadFile } from '../../types';
import { AtlasGqlAttachmentRecordType, AtlasGqlInspectionDraftImage } from 'types/atlas-graphql';
import { EditAttachmentForm } from 'components/RecordAttachments/EditAttachmentModal/EditAttachmentForm';
import { useUploadInspectionDataModalContext } from 'horizon/routes/Inspections/InspectionsUploadManager/Context/UploadInspectionDataModalContext';
import { ValidationTable } from 'horizon/routes/Inspections/InspectionsUploadManager/UploadInspectionDataModal.style';
import { TTableColumnDef } from 'horizon/types/TableColumnDef';
import { STANDARD_COLUMN_SCROLL_Y } from 'utils/constants';
import { CheckCircleTwoTone, CloseCircleTwoTone, WarningTwoTone } from '@ant-design5/icons';
import { None } from 'components/data/helpers';

const DEFAULT_ITEM_SIZE = 35;
const TALL_ITEM_SIZE = 200;

interface Data {
  fileName: string;
  listRef: React.Ref<any>;
  onRemove: (name: string) => void;
  selectedFiles: UploadFile[];
  setSelectedFiles: any;
  uploading?: boolean;
  validFilenames: string[];
  displayAttachmentOptions?: boolean;
  attachmentRecordParentType?: AtlasGqlAttachmentRecordType;
}

interface SelectedFileItemProps {
  data: Data;
  index: number;
  style?: React.CSSProperties;
}

type MatchStatus = {
  status: 'success' | 'warning' | 'error';
  message?: string;
};

const SelectedFileItem = memo(({ data, index, style }: SelectedFileItemProps) => {
  const theme = useTheme();

  const {
    validFilenames,
    selectedFiles,
    setSelectedFiles,
    uploading,
    onRemove,
    listRef,
    displayAttachmentOptions = false,
    attachmentRecordParentType = AtlasGqlAttachmentRecordType.Other,
  } = data;
  const file = selectedFiles[index];
  const { status } = file;

  const loadingIcon = <LoadingOutlined style={{ color: theme.teal }} spin />;
  const completeIcon = <CheckCircleOutlined style={{ color: theme.green }} />;
  const warningIcon = <WarningOutlined style={{ color: theme.red }} />;
  const pendingIcon = <ClockCircleOutlined />;

  // Callback to handle mapping the form values back to the files
  // so that they can be passed with the file data to the mutation call.
  const handleAttachmentValuesChange = () => {
    const values = form.getFieldsValue();
    if (setSelectedFiles) {
      const tempSelectedFiles = [...selectedFiles];
      tempSelectedFiles[index] = { ...file, ...values };
      setSelectedFiles(tempSelectedFiles);
    }
  };

  // Instantiate the form
  const [form] = Form.useForm();

  return (
    <SelectedFile key={file.sourceName + index} style={style}>
      <StyledFileNameRow>
        <StyledFilename invalid={!validFilenames.includes(file.sourceName)}>
          {<b>{file.sourceName}</b>}
        </StyledFilename>
        {uploading ? (
          status === 'error' ? (
            warningIcon
          ) : status === 'completed' ? (
            completeIcon
          ) : status === 'uploading' ? (
            loadingIcon
          ) : (
            pendingIcon
          )
        ) : (
          <StyledRemoveButton
            type="link"
            danger={true}
            value={file.sourceName}
            icon={<CloseOutlined />}
            onClick={e => {
              onRemove((e.currentTarget as HTMLButtonElement).value);
              (listRef as any)?.current?.resetAfterIndex(index, false);
            }}
          />
        )}
      </StyledFileNameRow>
      {displayAttachmentOptions && (
        <EditAttachmentForm
          form={form}
          handleChange={handleAttachmentValuesChange}
          categoryOnChange={handleAttachmentValuesChange}
          parentType={attachmentRecordParentType}
          disabled={uploading || !file.valid}
          /**
           * Pull default values from selectedFiles data. Initially these will be
           * undefined, but this prevents fields from appearing empty after
           * scrolling around the virtualized list and unmounting this component.
           */
          defaultCategoryId={selectedFiles[index].category}
          defaultDescription={selectedFiles[index].description}
        />
      )}
    </SelectedFile>
  );
}, areEqual);

// Memoized wrapper around the set of data that each list item will need
const createItemData = memoize(
  (
    validFilenames,
    selectedFiles,
    setSelectedFiles,
    uploading,
    fileName,
    onRemove,
    listRef,
    displayAttachmentOptions,
    attachmentRecordParentType
  ) => ({
    validFilenames,
    selectedFiles,
    setSelectedFiles,
    uploading,
    fileName,
    onRemove,
    listRef,
    displayAttachmentOptions,
    attachmentRecordParentType,
  })
);

export interface SelectedFileListProps {
  errorMessage?: React.ReactNode;
  fileName: string;
  filteredFiles: FilteredFiles;
  corruptFiles: string[];
  existingDraftImages?: AtlasGqlInspectionDraftImage[] | null;
  onRemove: (name: string) => void;
  showErrorSummary?: boolean;
  uploading?: boolean;
  validFiles: UploadFile[];
  displayAttachmentOptions?: boolean;
  attachmentRecordParentType?: AtlasGqlAttachmentRecordType;
  isBulkUpload?: boolean;
  showDragAndDropDirectoryError?: boolean;
  height?: number;
  // Set width to null if the list should take up the full space provided.
  width?: number | null;
}

export function SelectedFileList({
  errorMessage,
  fileName,
  filteredFiles,
  corruptFiles = [],
  existingDraftImages,
  onRemove,
  showErrorSummary,
  uploading,
  validFiles,
  displayAttachmentOptions = false,
  attachmentRecordParentType,
  isBulkUpload,
  showDragAndDropDirectoryError,
  height = 330,
  width = 496,
}: SelectedFileListProps) {
  const theme = useTheme();
  const { selectedFiles, setSelectedFiles } = useUploadContext();
  const validFilesCount = validFiles.length;
  const validFilenames = validFiles.map(file => file.sourceName);
  const invalidCount = selectedFiles.length - validFilesCount;

  const { imageDetailsContext } = useUploadInspectionDataModalContext();
  const {
    previewData: imageDetailsPreviewData,
    imagePathFieldName,
    setImageMissingForSomeMetadata,
  } = imageDetailsContext;

  function itemKey(index: number, data: { selectedFiles: UploadFile[] }) {
    // Find the item at the specified index.
    // In this case "data" is an Array that was passed to List as "itemData".
    const itemKey = data?.selectedFiles[index].sourceName;

    // Return a value that uniquely identifies this item.
    // Typically this will be a UID of some sort.
    return itemKey;
  }

  const listRef = useRef<any>();

  const itemData = createItemData(
    validFilenames,
    selectedFiles,
    setSelectedFiles,
    uploading,
    fileName,
    onRemove,
    listRef,
    displayAttachmentOptions,
    attachmentRecordParentType
  );

  const finalInvalidCount =
    corruptFiles.length === 0 ? invalidCount : invalidCount - corruptFiles.length;

  const previewTableProps: {
    columns: TTableColumnDef[];
    dataSource: (Partial<UploadFile> & {
      matchedDetailsPath: string | undefined;
      matchStatus: MatchStatus;
    })[];
  } | null = useMemo(() => {
    if (!imageDetailsPreviewData?.length) {
      return null;
    }

    const columns: TTableColumnDef[] = [
      {
        key: 'uploadedFile',
        title: 'Uploaded File',
        dataIndex: 'name',
        ellipsis: { showTitle: true },
        render: value => value ?? <None />,
      },
      {
        key: 'matchedDetailsPath',
        title: 'Matched Details Path',
        dataIndex: 'matchedDetailsPath',
        ellipsis: { showTitle: true },
        render: value => value ?? <None />,
      },
      {
        key: 'matchStatus',
        title: 'Match Success',
        dataIndex: 'matchStatus',
        align: 'center',
        width: 140,
        render: ({ status, message }: MatchStatus) => {
          return status === 'success' ? (
            <CheckCircleTwoTone twoToneColor={theme.green} />
          ) : status === 'warning' ? (
            <Tooltip title={message}>
              <WarningTwoTone twoToneColor={theme.yellow} />
            </Tooltip>
          ) : (
            <Tooltip title={message}>
              <CloseCircleTwoTone twoToneColor={theme.red} />
            </Tooltip>
          );
        },
      },
      {
        key: 'remove',
        title: 'Remove',
        dataIndex: 'sourceName',
        align: 'center',
        width: 84,
        render: (value, record) => {
          if (value) {
            return (
              <>
                {uploading ? (
                  record.status === 'error' ? (
                    <WarningOutlined style={{ color: theme.red }} />
                  ) : record.status === 'completed' ? (
                    <CheckCircleOutlined style={{ color: theme.green }} />
                  ) : record.status === 'uploading' ? (
                    <LoadingOutlined style={{ color: theme.teal }} spin />
                  ) : (
                    <ClockCircleOutlined />
                  )
                ) : (
                  <StyledRemoveButton
                    type="link"
                    danger={true}
                    icon={<CloseOutlined />}
                    onClick={() => {
                      onRemove(value);
                    }}
                  />
                )}
              </>
            );
          }

          // No remove button for rows with missing files or existing draft images
          return null;
        },
      },
    ];

    const allFileNames = uniq([
      ...selectedFiles.map(({ name }) => name),
      ...(existingDraftImages ?? []).map(({ path }) => path),
      ...imageDetailsPreviewData.map(row => row[imagePathFieldName]),
    ]);

    const dataSource = allFileNames.reduce<
      (Partial<UploadFile> & {
        matchedDetailsPath: string | undefined;
        matchStatus: MatchStatus;
      })[]
    >((acc, name) => {
      const selectedFile = selectedFiles.find(f => f.name === name);
      const existingDraftImage = existingDraftImages?.find(i => i.path === name);
      const matchedDetails = imageDetailsPreviewData.find(row => row[imagePathFieldName] === name);

      return [
        ...acc,
        ...(selectedFile
          ? [
              {
                ...selectedFile,
                matchedDetailsPath: matchedDetails
                  ? String(matchedDetails[imagePathFieldName])
                  : undefined,
                matchStatus: (!!matchedDetails
                  ? {
                      status: 'success',
                    }
                  : {
                      status: 'warning',
                      message:
                        'This image does not exist in the details file. It will not be uploaded.',
                    }) as MatchStatus,
              },
            ]
          : []),
        ...(existingDraftImage
          ? [
              {
                name: `${existingDraftImage.path} (existing)`,
                matchedDetailsPath: matchedDetails
                  ? String(matchedDetails[imagePathFieldName])
                  : undefined,
                matchStatus: (!!matchedDetails
                  ? {
                      status: 'success',
                    }
                  : {
                      status: 'warning',
                      message:
                        'This image does not exist in the details file. Metadata will not be applied.',
                    }) as MatchStatus,
              },
            ]
          : []),
        ...(!selectedFile && !existingDraftImage
          ? [
              {
                matchedDetailsPath: matchedDetails
                  ? String(matchedDetails[imagePathFieldName])
                  : undefined,
                matchStatus: {
                  status: 'error',
                  message:
                    'No image maps to this metadata. Images are required for each row of details.',
                } as MatchStatus,
              },
            ]
          : []),
      ];
    }, []);

    return { columns, dataSource };
  }, [imageDetailsPreviewData, selectedFiles, existingDraftImages, onRemove, theme]);

  useEffect(() => {
    const hasErrors = !!previewTableProps?.dataSource.some(
      row => row.matchStatus.status === 'error'
    );
    setImageMissingForSomeMetadata(hasErrors);
  }, [previewTableProps]);

  return (
    <Fragment>
      {showErrorSummary ? (
        <ErrorSummary validFilesCount={validFilesCount} />
      ) : (
        <Fragment>
          {errorMessage && <ErrorAlert message={errorMessage} />}

          {uploading ? (
            <FileUploadProgressSummary />
          ) : (
            <Fragment>
              {corruptFiles.length > 0 && (
                <WarningAlert
                  message={`${corruptFiles.length} files will not be uploaded because they are corrupt.`}
                />
              )}
              {finalInvalidCount > 0 && (
                <WarningAlert
                  message={`${finalInvalidCount} files will not be uploaded because they are invalid.`}
                />
              )}
              {isBulkUpload && validFilesCount == 0 && (
                <WarningAlert
                  message={`No valid files found. Images will be downloaded from URL's if defined in the provided details file.`}
                />
              )}
              {filteredFiles.status && (
                <WarningAlert
                  message={`${filteredFiles.count} .DS_Store files have been filtered out because they are not necessary`}
                />
              )}
              {showDragAndDropDirectoryError && (
                <WarningAlert message={DRAG_AND_DROP_DIRECTORY_ERROR} />
              )}
              <FilesSelected>
                {selectedFiles.length} {`file${selectedFiles.length === 1 ? '' : 's'} selected`}
              </FilesSelected>
            </Fragment>
          )}

          {previewTableProps ? (
            <ValidationTable {...previewTableProps} scroll={{ y: STANDARD_COLUMN_SCROLL_Y.y }} />
          ) : (
            <StyledList
              itemCount={selectedFiles.length}
              // The itemSize needs to be taller if displaying the Attachment details sub-form
              itemSize={() => (displayAttachmentOptions ? TALL_ITEM_SIZE : DEFAULT_ITEM_SIZE)}
              itemData={itemData}
              height={height}
              width={width}
              itemKey={itemKey}
              ref={listRef}
            >
              {SelectedFileItem as any}
            </StyledList>
          )}
        </Fragment>
      )}
    </Fragment>
  );
}

interface ErrorSummaryProps {
  validFilesCount: number;
}

function ErrorSummary({ validFilesCount }: ErrorSummaryProps) {
  const { selectedFiles } = useUploadContext();
  const errors = selectedFiles.filter(({ status }) => status === 'error');

  return (
    <Fragment>
      <p>{validFilesCount - errors.length} files uploaded successfully.</p>
      <ErrorWrapper>
        <p>The following files were not uploaded:</p>
        <ul>
          {errors.map(file => (
            <li key={file.sourceName}>{file.sourceName}</li>
          ))}
        </ul>
      </ErrorWrapper>
    </Fragment>
  );
}
